import React from 'react';
import { 
  Smartphone, Calendar, 
  Settings, PlayCircle, MessageCircle, HelpCircle, Video 
} from 'lucide-react';

const AjudaPage = () => {
  const passos = [
    {
      id: 1,
      titulo: "1. Identificação e Mensagem",
      desc: "Vá em 'Configurações' e dê um nome ao seu aparelho (ex: clinica_doc). Defina também a mensagem de saudação que o robô enviará.",
      icon: <Settings className="text-blue-600" size={24} />,
      link: "Como fazer: Digite os dados e clique em 'Salvar Alterações' no final da página."
    },
    {
      id: 2,
      titulo: "2. Conexão com WhatsApp",
      desc: "Escolha 'QR Code' (se estiver no PC) ou 'Código' (se estiver no Celular). Siga as instruções para parear seu WhatsApp.",
      icon: <Smartphone className="text-emerald-600" size={24} />,
      link: "Importante: O celular deve estar com internet ativa durante o pareamento."
    },
    {
      id: 3,
      titulo: "3. Sincronização da Agenda",
      desc: "Conecte sua conta do Google Agenda. O robô lerá seus compromissos existentes para nunca marcar dois pacientes no mesmo horário.",
      icon: <Calendar className="text-purple-600" size={24} />,
      link: "Dica: Verifique se os horários de atendimento na aba 'Fluxo da Agenda' estão corretos."
    }
  ];

  const tutoriais = [
    {
      id: 1,
      titulo: "Como conectar seu WhatsApp",
      desc: "Aprenda a gerar o QR Code e parear seu número com o sistema em poucos segundos.",
      videoId: "ZoILBNjx-eA"
    },
    {
      id: 2,
      titulo: "Integrando o Google Agenda",
      desc: "Passo a passo de como vincular seu calendário para evitar choque de horários.",
      videoId: "wjXrYKmazq4"
    },
    {
      id: 3,
      titulo: "Configuração de Horários",
      desc: "Um tour completo para realizar a configuração de horários.",
      videoId: "SoePIcSdfk4" 
    }
  ];

  return (
    <div className="max-w-[800px] mx-auto font-sans p-5 box-border">
      
      {/* CABEÇALHO */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 sm:p-10 rounded-[32px] text-white mb-8 shadow-[0_20px_40px_rgba(0,0,0,0.1)]">
        <div className="flex items-center gap-2.5 mb-4">
          <PlayCircle size={24} className="text-blue-400" />
          <span className="text-xs font-black tracking-widest uppercase">Guia de Início Rápido</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-black mb-3">Olá! Vamos configurar sua secretária?</h1>
        <p className="text-sm text-slate-400 leading-relaxed font-medium">
          Siga o checklist abaixo para ativar o <strong>Agenda Sincro</strong>. Em menos de 5 minutos seu WhatsApp estará automatizado.
        </p>
      </div>

      {/* CHECKLIST DE INÍCIO */}
      <div className="flex flex-col gap-4">
        {passos.map((passo) => (
          <div key={passo.id} className="bg-white border border-slate-100 p-6 sm:p-8 rounded-3xl flex flex-col sm:flex-row gap-5 transition-all duration-300 hover:translate-x-2 hover:border-blue-600 group">
            <div className="w-16 h-16 bg-slate-50 rounded-[18px] flex items-center justify-center shrink-0 transition-colors group-hover:bg-blue-50">
              {passo.icon}
            </div>
            <div className="flex flex-col justify-center">
              <h3 className="text-base font-black text-slate-900 mb-2">{passo.titulo}</h3>
              <p className="text-sm text-slate-500 leading-relaxed font-medium mb-3">{passo.desc}</p>
              <div className="self-start text-[10px] font-black text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full uppercase">
                {passo.link}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* NOVA SEÇÃO: TUTORIAIS EM VÍDEO */}
      <div className="mt-12 pt-8 border-t border-slate-200">
        <h2 className="text-lg font-black text-slate-900 flex items-center gap-2.5 mb-2">
          <Video size={20} className="text-blue-600" /> Tutoriais em Vídeo
        </h2>
        <p className="text-sm text-slate-500 font-medium">Assista na prática como utilizar todas as ferramentas do sistema.</p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
          {tutoriais.map((video) => (
            <div key={video.id} className="bg-white border border-slate-200 rounded-3xl overflow-hidden transition-all duration-300 flex flex-col hover:border-blue-600 hover:-translate-y-1.5 hover:shadow-[0_15px_30px_-10px_rgba(37,99,235,0.15)] group">
              {/* O aspect-video do Tailwind resolve o problema da altura do iframe */}
              <div className="relative w-full aspect-video bg-slate-100 overflow-hidden">
                <iframe 
                  className="absolute top-0 left-0 w-full h-full border-none"
                  src={`https://www.youtube.com/embed/${video.videoId}?rel=0`} 
                  title={video.titulo}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
              <div className="p-5 flex-grow flex flex-col justify-start">
                <h4 className="text-[15px] font-extrabold text-slate-900 mb-1.5 transition-colors group-hover:text-blue-600">{video.titulo}</h4>
                <p className="text-[13px] text-slate-500 leading-relaxed font-medium m-0">{video.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className="mt-12 pt-8 border-t border-slate-200">
        <h2 className="text-lg font-black text-slate-900 mb-5 flex items-center gap-2.5">
          <HelpCircle size={20} className="text-slate-400" /> Dúvidas Frequentes
        </h2>
        <div className="flex flex-col gap-4">
          <details className="bg-slate-50 p-4 sm:p-5 rounded-2xl cursor-pointer group">
            <summary className="font-extrabold text-[13px] text-slate-700 outline-none list-none flex justify-between items-center">
              O robô atende em grupos?
              <span className="text-blue-600 transition-transform group-open:rotate-180">+</span>
            </summary>
            <p className="text-[13px] text-slate-500 mt-3 mb-0 leading-relaxed font-medium">Não. O assistente funciona apenas em conversas individuais para garantir a privacidade dos seus pacientes.</p>
          </details>
          <details className="bg-slate-50 p-4 sm:p-5 rounded-2xl cursor-pointer group">
            <summary className="font-extrabold text-[13px] text-slate-700 outline-none list-none flex justify-between items-center">
              Posso usar o WhatsApp normalmente?
              <span className="text-blue-600 transition-transform group-open:rotate-180">+</span>
            </summary>
            <p className="text-[13px] text-slate-500 mt-3 mb-0 leading-relaxed font-medium">Sim! Você pode responder manualmente quando quiser. O robô detecta sua interferência e aguarda o tempo de 'Reset Bot' para atuar novamente.</p>
          </details>
        </div>
      </div>

      {/* CTA SUPORTE */}
      <div className="mt-10 text-center p-8 sm:p-10 bg-blue-50 rounded-3xl flex flex-col items-center">
        <MessageCircle size={32} className="text-blue-600 mb-3" />
        <h4 className="text-sm font-black text-slate-900 mb-1">Precisa de uma mãozinha extra?</h4>
        <p className="text-xs text-slate-500 font-medium mb-6">Nossa equipe de suporte está pronta para ajudar na sua primeira configuração.</p>
        <a 
          href="https://wa.me/558521807757" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-block no-underline bg-blue-600 hover:bg-blue-700 text-white border-none py-3 px-6 rounded-xl font-extrabold text-xs cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5 hover:shadow-blue-600/25"
        >
          Chamar no Suporte
        </a>
      </div>

    </div>
  );
};

export default AjudaPage;